// SAFE-WRITE-BEGIN: frontend/src/pages/valuation/_CompareValues.tsx
/**
 * M-DEMO — Compare values: one coloured column per method (MD-3b), 5 across on
 * a desktop and 2 on a phone. A column shows its value large in its colour, or
 * a grey italic sentence (MD-6/7/8). Never red. Screen only.
 */
import type { ReactNode } from 'react'
import { money } from '@/utils/format'
import type { MethodEntry, MethodKey } from '@/types/valuationB3'
import { METHOD_CARDS, MD, methodSentence } from './_valueMethods'

/** An owner sees a value only when the method is available; a signer sees any value the server sent. */
export function shownValue(m: MethodEntry, signer: boolean): number | null {
  if (m.value == null) return null
  return m.status === 'available' || signer ? m.value : null
}

export function CompareValues({ methods, signer, inputs }: {
  methods: Record<MethodKey, MethodEntry>
  signer: boolean
  /** The cash and debt boxes, under the columns. */
  inputs: ReactNode
}) {
  return (
    <section className="rounded-lg border border-pal-line bg-pal-white p-5">
      <h2 className="text-lg font-semibold text-pal-navy">{MD.title}</h2>
      <p className="mt-1 text-sm text-pal-ink-soft">{MD.note}</p>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {METHOD_CARDS.map(card => {
          const m = methods[card.key]
          if (!m) return null
          const value = shownValue(m, signer)
          const sentence = methodSentence(m)
          return (
            <div
              key={card.key}
              role="group"
              aria-label={card.name}
              className="flex flex-col rounded-md p-3"
              style={{ borderTop: `5px solid ${card.color}`, backgroundColor: `${card.color}14` }}
            >
              <span className="text-sm font-semibold text-pal-ink">{card.name}</span>
              <span className="text-xs text-pal-ink-muted">{card.subtitle}</span>
              {value != null && (
                <span className="mt-3 text-2xl font-bold tracking-tight" style={{ color: card.color }}>
                  {money(value)}
                </span>
              )}
              {sentence && <span className="mt-3 text-sm italic text-pal-ink-muted">{sentence}</span>}
            </div>
          )
        })}
      </div>
      <div className="mt-4">{inputs}</div>
    </section>
  )
}
// SAFE-WRITE-EOF: frontend/src/pages/valuation/_CompareValues.tsx
